import { useEffect, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

const testimonials = [
  {
    quote:
      "Black Swan Productions have quickly become a trusted supplier and extension of our team. Their ability to listen, visualise and actively collaborate with ourselves in positioning de Novo to the wider market, has been critical and central to our growth strategy. I cannot recommend Haydn and the Black Swan team highly enough, and we continue to look forward to working together.",
    author: "de Novo Solutions",
  },
  {
    quote:
      "Working with Haydn and the Black Swan Team has been so easy. They are incredibly flexible and supportive of our vision for Captains Clubhouse. Haydn and Dewi in particular have taken the time to get to know us and understand what we want from each project. The podcast/brand stand offering is unlike anything else and really keeps us on track as a business. We highly recommend the Black Swan Team to all businesses looking to level up their Marketing, Communications and Outreach. Whether you are founders in the early stages or a developed business, Black Swan can and will help!",
    author: "Captains Clubhouse",
  },
  {
    quote:
      "The team at Black Swan Productions responded to a late plea to get a promotional video completed, and given the sensitive timeframes I could never have imagined the superb quality of work that would be produced. Both professional and personable, the team took their time to understand the product we needed to promote, and produced a video that showcases this. My Lifetime Care would highly recommend and look forward to working with Black Swan Productions again in the near future.",
    author: "My Lifetime Care",
  },
  {
    quote:
      "Working with Black Swan Productions was an absolute pleasure from start to finish. Haydn was incredibly helpful throughout the process and made filming our event and interviews feel seamless and stress-free. His creativity and depth of knowledge are truly unparalleled, and his guidance helped capture the event in a way that really reflected the atmosphere and the voices of everyone involved. Black Swan Productions is not only highly professional but also a fantastic team to collaborate with. Their approach to filming and storytelling meant every detail was handled with care, and we felt confident throughout that the event and interviews were in great hands. I would highly recommend Black Swan Productions to anyone looking for a team that combines creativity, technical expertise, and genuine dedication to capturing an event at its very best.",
    author: "Cardiff & Vale Credit Union",
  },
];

const ROTATE_MS = 5000;

export function Testimonial() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const goTo = useCallback((i: number) => {
    setIndex((i + testimonials.length) % testimonials.length);
  }, []);

  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % testimonials.length);
    }, ROTATE_MS);
    return () => clearInterval(id);
  }, [paused, index]);

  const active = testimonials[index];

  return (
    <section className="py-32 bg-black relative overflow-hidden">
      {/* Decorative large quote */}
      <div className="absolute -top-10 left-10 md:left-20 text-[20rem] font-serif text-white/[0.02] leading-none pointer-events-none select-none">
        &ldquo;
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div
          className="max-w-4xl mx-auto text-center"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocusCapture={() => setPaused(true)}
          onBlurCapture={() => setPaused(false)}
        >
          <div className="min-h-[22rem] sm:min-h-[20rem] flex flex-col items-center justify-center">
            <AnimatePresence mode="wait">
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
              >
                <p className="font-display text-xl md:text-2xl lg:text-3xl leading-relaxed font-medium text-white mb-12">
                  &ldquo;{active.quote}&rdquo;
                </p>

                <div className="flex flex-col items-center">
                  <div className="w-12 h-[1px] bg-primary mb-6"></div>
                  <p className="text-gray-300 text-sm tracking-widest uppercase">
                    {active.author}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center gap-6 mt-12">
            <button
              type="button"
              onClick={prev}
              aria-label="Previous testimonial"
              className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-primary hover:border-primary transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <div className="flex items-center">
              {testimonials.map((t, i) => (
                <button
                  key={t.author}
                  type="button"
                  onClick={() => goTo(i)}
                  aria-label={`Show testimonial from ${t.author}`}
                  aria-current={i === index}
                  className="group/dot flex h-8 min-w-8 items-center justify-center px-[7px] focus-visible:outline-none"
                >
                  <span
                    aria-hidden="true"
                    className={`h-2 rounded-full transition-all duration-300 group-focus-visible/dot:ring-2 group-focus-visible/dot:ring-primary ${
                      i === index
                        ? "w-8 bg-primary"
                        : "w-2 bg-white/30 group-hover/dot:bg-white/60"
                    }`}
                  />
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={next}
              aria-label="Next testimonial"
              className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-primary hover:border-primary transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
